<template>
  <div>
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <router-link to="/tenants/paySlipList" class="small d-inline-block mb-2">&larr; Payslips</router-link>
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Payslip ${obj.no || ""}` : "New payslip" }}
              <CBadge
                v-if="obj.statusDescription"
                :color="hrStatusColor('payslip', obj.statusDescription)"
                class="ml-2 align-middle"
              >
                {{ hrStatusLabel('payslip', obj.statusDescription) }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{ selectedEmployee && selectedEmployee.fullName ? selectedEmployee.fullName : "Choose an employee and enter their pay for the period" }}
            </p>
          </CCol>
          <CCol md="6" class="d-flex flex-wrap justify-content-md-end align-items-center mt-3 mt-md-0">
            <CButton
              v-if="obj.id && obj.profileId"
              color="light"
              class="mr-2 mb-2 mb-md-0"
              @click="calculateYTD"
              :disabled="calculatingYTD"
            >
              <CSpinner v-if="calculatingYTD" size="sm" class="mr-1" />
              <CIcon v-else name="cil-calculator" class="mr-1" />
              {{ calculatingYTD ? "Calculating…" : "Calculate YTD" }}
            </CButton>
            <CDropdown
              v-if="obj.id && computedPaySlipStates.length"
              color="light"
              toggler-text="Change status"
              placement="bottom-end"
              class="mr-2 mb-2 mb-md-0"
            >
              <CDropdownItem
                v-for="status in computedPaySlipStates"
                :key="status.value"
                @click="askChangeState(status)"
              >
                {{ stateActionLabel(status.label) }}
              </CDropdownItem>
            </CDropdown>
            <CButton color="light" class="mr-2 mb-2 mb-md-0" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton color="primary" class="mb-2 mb-md-0" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Create payslip" }}
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody>
            <CForm>
              <!-- Employee Selection -->
              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Employee *
                </CCol>
                <CCol sm="9">
                  <v-select
                    style="width: 100%"
                    v-model="selectedEmployee"
                    :options="employeeItems"
                    placeholder="Choose an employee"
                    :readonly="!!obj.id"
                  >
                    <template #option="option">
                      <div>
                        <div class="font-weight-bold">{{ option.fullName }}</div>
                        <small class="text-muted">{{ option.email }}</small>
                      </div>
                    </template>
                    <template #selected-option="option">
                      <span>{{ option.fullName }} <small class="text-muted">({{ option.email }})</small></span>
                    </template>
                  </v-select>
                  <small v-if="employeeError" class="text-danger d-block mt-1">{{ employeeError }}</small>
                </CCol>
              </CRow>

              <!-- PaySlip Details -->
              <CInput
                label="Payslip no."
                horizontal
                v-model="obj.no"
                readonly
              />
              <CInput
                label="Reference"
                horizontal
                v-model="obj.reference"
              />

              <CInput
                horizontal
                label="Pay date"
                type="date"
                :value="computePayDate"
                @change="setPayDate"
              />

              <CInput
                label="Pay period"
                horizontal
                v-model="obj.payPeriod"
                placeholder="For example, January 2026"
              />

              <!-- Employee Details Section -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">Employee details</h2>

              <CInput
                label="Employee no."
                horizontal
                v-model="obj.employeeNo"
                readonly
              />
              <CInput
                label="IC number"
                horizontal
                v-model="obj.icNumber"
                readonly
              />
              <CInput
                label="Position"
                horizontal
                v-model="obj.position"
                readonly
              />
              <CInput
                label="Department"
                horizontal
                v-model="obj.department"
                readonly
              />
              <CInput
                label="Join date"
                horizontal
                type="date"
                :value="computeJoinDate"
                readonly
              />

              <!-- Statutory Numbers -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">Statutory numbers</h2>

              <CInput
                label="EPF no."
                horizontal
                v-model="obj.epfNo"
              />
              <CInput
                label="SOCSO no."
                horizontal
                v-model="obj.socsoNo"
              />
              <CInput
                label="Tax no."
                horizontal
                v-model="obj.taxNo"
              />

              <!-- Bank Details -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">Bank details</h2>

              <CInput
                label="Bank name"
                horizontal
                v-model="obj.bankName"
              />
              <CInput
                label="Account name"
                horizontal
                v-model="obj.bankAccountName"
              />
              <CInput
                label="Account no."
                horizontal
                v-model="obj.bankAccountNo"
              />

              <!-- Earnings & Deductions -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">Pay</h2>

              <CInput
                label="Gross pay"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.grossAmount"
              />
              <CInput
                label="Net pay"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.totalAmount"
              />

              <!-- EPF -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">EPF <HrGlossaryTip term="EPF" /></h2>

              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Deduct EPF
                </CCol>
                <CCol sm="9">
                  <CSwitch
                    class="mr-1"
                    color="primary"
                    :checked.sync="obj.isEnableEpf"
                  />
                </CCol>
              </CRow>
              <CInput
                label="Employee EPF"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.employeeEpfAmount"
                :disabled="!obj.isEnableEpf"
              />
              <CInput
                label="Employer EPF"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.employerEpfAmount"
                :disabled="!obj.isEnableEpf"
              />

              <!-- SOCSO -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">SOCSO <HrGlossaryTip term="SOCSO" /></h2>

              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Deduct SOCSO
                </CCol>
                <CCol sm="9">
                  <CSwitch
                    class="mr-1"
                    color="primary"
                    :checked.sync="obj.isEnableSocso"
                  />
                </CCol>
              </CRow>
              <CInput
                label="Employee SOCSO"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.employeeSocsoAmount"
                :disabled="!obj.isEnableSocso"
              />
              <CInput
                label="Employer SOCSO"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.employerSocsoAmount"
                :disabled="!obj.isEnableSocso"
              />

              <!-- EIS -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">EIS <HrGlossaryTip term="EIS" /></h2>

              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Deduct EIS
                </CCol>
                <CCol sm="9">
                  <CSwitch
                    class="mr-1"
                    color="primary"
                    :checked.sync="obj.isEnableEis"
                  />
                </CCol>
              </CRow>
              <CInput
                label="Employee EIS"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.employeeEisAmount"
                :disabled="!obj.isEnableEis"
              />
              <CInput
                label="Employer EIS"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.employerEisAmount"
                :disabled="!obj.isEnableEis"
              />

              <!-- Tax & Zakat -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">Tax and zakat</h2>

              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Deduct PCB <HrGlossaryTip term="PCB" />
                </CCol>
                <CCol sm="9">
                  <CSwitch
                    class="mr-1"
                    color="primary"
                    :checked.sync="obj.isEnableTax"
                  />
                </CCol>
              </CRow>
              <CInput
                label="PCB amount"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.taxAmount"
                :disabled="!obj.isEnableTax"
              />

              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Deduct zakat <HrGlossaryTip term="Zakat" />
                </CCol>
                <CCol sm="9">
                  <CSwitch
                    class="mr-1"
                    color="primary"
                    :checked.sync="obj.isEnableZakat"
                  />
                </CCol>
              </CRow>
              <CInput
                label="Zakat amount"
                horizontal
                type="number"
                step="0.01"
                min="0"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.zakatAmount"
                :disabled="!obj.isEnableZakat"
              />

              <!-- Leave Balances -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">Leave balances (days)</h2>

              <CInput
                label="Annual leave"
                horizontal
                type="number"
                step="0.5"
                v-model="obj.annualLeaveBalance"
              />
              <CInput
                label="Medical leave"
                horizontal
                type="number"
                step="0.5"
                v-model="obj.medicalLeaveBalance"
              />
              <CInput
                label="Sick leave"
                horizontal
                type="number"
                step="0.5"
                v-model="obj.sickLeaveBalance"
              />

              <!-- Payment Details -->
              <hr class="my-4" />
              <h2 class="h6 mb-3">Payment details</h2>

              <CInput
                horizontal
                label="Payment date"
                type="date"
                :value="computePaymentDate"
                @change="setPaymentDate"
              />
              <CInput
                label="Payment reference"
                horizontal
                v-model="obj.paymentReferenceNo"
              />
              <CInput
                label="Payment method"
                horizontal
                v-model="obj.paymentMethod"
                placeholder="For example, bank transfer"
              />

              <!-- Remarks -->
              <hr class="my-4" />
              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">
                  Remarks
                </CCol>
                <CCol sm="9">
                  <CTextarea
                    placeholder="Anything the employee should know"
                    rows="3"
                    v-model="obj.remarks"
                  />
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- YTD (Year-to-Date) Summary Card -->
    <CRow v-if="ytdData">
      <CCol sm="12">
        <CCard>
          <CCardHeader>
            <strong>Year to date {{ ytdData.year }}</strong>
            <HrGlossaryTip term="YTD" />
          </CCardHeader>
          <CCardBody>
            <div class="table-responsive"><table class="table table-bordered">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Payslips this year</strong></td>
                  <td class="text-right">{{ ytdData.payslipCount }}</td>
                </tr>
                <tr>
                  <td>Gross pay</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdGrossAmount) }}</td>
                </tr>
                <tr>
                  <td>EPF (employee)</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdEpfEmployee) }}</td>
                </tr>
                <tr>
                  <td>EPF (employer)</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdEpfEmployer) }}</td>
                </tr>
                <tr>
                  <td>SOCSO (employee)</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdSocsoEmployee) }}</td>
                </tr>
                <tr>
                  <td>SOCSO (employer)</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdSocsoEmployer) }}</td>
                </tr>
                <tr>
                  <td>EIS (employee)</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdEisEmployee) }}</td>
                </tr>
                <tr>
                  <td>EIS (employer)</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdEisEmployer) }}</td>
                </tr>
                <tr>
                  <td>PCB</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdTax) }}</td>
                </tr>
                <tr>
                  <td>Zakat</td>
                  <td class="text-right">{{ formatCurrency(ytdData.ytdZakat) }}</td>
                </tr>
                <tr class="table-active">
                  <td><strong>Net pay</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(ytdData.ytdNetPay) }}</strong></td>
                </tr>
              </tbody>
            </table></div>
            <small class="text-muted">
              Includes every payslip for this employee in {{ ytdData.year }}. You'll need these totals for the EA form.
            </small>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- PaySlip PDF View -->
    <CRow v-if="obj.id">
      <CCol sm="12">
        <WidgetsReportPayslip :payslip="computedPreviewPayslip" />
      </CCol>
    </CRow>

    <!-- Action Buttons -->
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody>
            <div class="d-flex justify-content-end">
              <CButton color="light" class="mr-2" :disabled="saving" @click="cancel">
                Cancel
              </CButton>
              <CButton color="primary" :disabled="saving" @click="submit">
                <CSpinner v-if="saving" size="sm" class="mr-1" />
                {{ obj.id ? "Save" : "Create payslip" }}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Status change confirmation -->
    <CModal
      :title="statusConfirmTitle"
      :show.sync="statusConfirmModal"
      :close-on-backdrop="!changingState"
    >
      <p class="mb-0">{{ statusConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="changingState" @click="statusConfirmModal = false">
          {{ pendingStatus && pendingStatus.label === "Cancelled" ? "Keep it" : "Cancel" }}
        </CButton>
        <CButton
          :color="pendingStatus && pendingStatus.label === 'Cancelled' ? 'danger' : 'primary'"
          :disabled="changingState"
          @click="confirmChangeState"
        >
          <CSpinner v-if="changingState" size="sm" class="mr-1" />
          {{ pendingStatus ? stateActionLabel(pendingStatus.label) : "" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PaySlipApi from "@/lib/paySlipApi";
import { asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { hrStatusMixin, hrStatusLabel } from "@/utils/hrStatusBadges";
import HrGlossaryTip from "../components/HrGlossaryTip";
import ProfileApi from "../../lib/profileApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import WidgetsReportPayslip from "../widgets/WidgetsReportPayslip";

export default {
  name: "PaySlip",
  components: {
    vSelect,
    WidgetsReportPayslip,
    HrGlossaryTip,
  },
  mixins: [hrStatusMixin],
  data: () => {
    return {
      selectedSlipState: 0,
      slipStates: [],

      profileApi: new ProfileApi(),

      selectedEmployee: null,
      employeeItems: [],

      payDate: new Date(),
      paymentDate: null,
      joinDate: null,

      selectedSlipType: 0,
      slipTypes: [],
      saving: false,
      employeeError: "",
      statusConfirmModal: false,
      changingState: false,
      pendingStatus: null,
      obj: {},
      submitted: false,
      api: new PaySlipApi(),

      // YTD calculation
      calculatingYTD: false,
      ytdData: null,
    };
  },
  mounted() {
    var self = this;
    this.fetchSlipStates();
    this.fetchSlipTypes();
    this.refreshEmployee();
    self.resetObj();
  },
  computed: {
    computePayDate() {
      return asDateString(this.payDate);
    },
    computePaymentDate() {
      return this.paymentDate ? asDateString(this.paymentDate) : "";
    },
    computeJoinDate() {
      return this.joinDate ? asDateString(this.joinDate) : "";
    },
    statusConfirmTitle() {
      if (!this.pendingStatus) return "";
      const no = this.obj.no ? ` ${this.obj.no}` : "";
      if (this.pendingStatus.label === "Approve") return `Approve payslip${no}?`;
      if (this.pendingStatus.label === "Paid") return `Mark payslip${no} as paid?`;
      if (this.pendingStatus.label === "Cancelled") return `Cancel payslip${no}?`;
      return `Mark payslip${no} as ${hrStatusLabel("payslip", this.pendingStatus.label).toLowerCase()}?`;
    },
    statusConfirmMessage() {
      if (!this.pendingStatus) return "";
      const who = this.selectedEmployee && this.selectedEmployee.fullName ? ` for ${this.selectedEmployee.fullName}` : "";
      const net = `RM ${formatMyr(this.obj.totalAmount)}`;
      switch (this.pendingStatus.label) {
        case "Approve":
          return `The payslip${who} (net pay ${net}) will be approved and can then be marked as paid.`;
        case "Paid":
          return `The payslip${who} will be recorded as paid (net pay ${net}).`;
        case "Cancelled":
          return `The payslip${who} (net pay ${net}) will be cancelled and can't be paid.`;
        default:
          return `The payslip${who} will be marked as ${hrStatusLabel("payslip", this.pendingStatus.label).toLowerCase()}.`;
      }
    },
    computedPaySlipStates() {
      let statuses = this.slipStates.filter(
        (status) => status.label != this.obj.statusDescription
      );

      if (this.obj.id == null) return [];

      if (this.obj.statusDescription === "Draft")
        return statuses.filter((status) => status.label == "Approve");
      if (this.obj.statusDescription === "Approve")
        return statuses.filter(
          (status) => status.label == "Paid" || status.label == "Cancelled"
        );
      else return [];
    },
    // Preview computed property - ensures business object with logo is present
    computedPreviewPayslip() {
      return {
        ...this.obj,
        business: auth.getLocalBusiness(),
      };
    },
  },
  watch: {
    selectedEmployee(newEmployee) {
      if (newEmployee && !this.obj.id) {
        // Auto-populate employee details when creating new payslip
        this.obj.employeeNo = newEmployee.employeeNo || '';
        this.obj.icNumber = newEmployee.icNumber || '';
        this.obj.position = newEmployee.position || '';
        this.obj.department = newEmployee.department || '';
        this.obj.epfNo = newEmployee.epfNumber || '';
        this.obj.socsoNo = newEmployee.socsoNumber || '';
        this.obj.taxNo = newEmployee.taxNumber || '';
        this.obj.bankName = newEmployee.bankName || '';
        this.obj.bankAccountName = newEmployee.bankAccountName || '';
        this.obj.bankAccountNo = newEmployee.bankAccountNumber || '';
        this.joinDate = newEmployee.joinDate || null;

        // Auto-populate leave balances
        this.obj.annualLeaveBalance = newEmployee.annualLeaveBalance || 0;
        this.obj.medicalLeaveBalance = newEmployee.medicalLeaveBalance || 0;
        this.obj.sickLeaveBalance = newEmployee.sickLeaveBalance || 0;
      }
    },
  },
  methods: {
    getCurrency() {
      var business = auth.getLocalBusiness();
      return business?.currency || 'MYR';
    },
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    stateActionLabel(name) {
      if (name === "Approve") return "Approve payslip";
      if (name === "Paid") return "Mark as paid";
      if (name === "Cancelled") return "Cancel payslip";
      return hrStatusLabel("payslip", name);
    },
    async calculateYTD() {
      const self = this;

      if (!self.obj.profileId) {
        self.toast("Warning", "Choose an employee first", "warning");
        return;
      }

      const year = parseInt((asDateString(self.payDate) || asDateString(new Date())).substring(0, 4), 10);

      self.calculatingYTD = true;

      try {
        const response = await self.api.calculateYtd(self.obj.profileId, year);
        self.ytdData = response.result;

        self.toast(
          "Success",
          `Year-to-date totals for ${year} updated`,
          "success"
        );
      } catch (error) {
        console.error("YTD calculation error:", error);
        self.toast("Error", "Couldn't calculate the year-to-date totals. Try again.", "danger");
      } finally {
        self.calculatingYTD = false;
      }
    },
    refreshEmployee() {
      var self = this;
      self.profileApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.employeeItems = response.result;
        })
        .catch(({ data }) => {});
    },
    setPayDate(e) {
      this.payDate = e;
    },
    setPaymentDate(e) {
      this.paymentDate = e ? e : null;
    },
    fetchSlipStates() {
      var self = this;
      self.api
        .getSlipStates()
        .then((response) => {
          var obj = response.result;
          this.slipStates = obj.map((state) => ({
            value: state.id,
            label: state.name,
          }));
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load everything on this page. Refresh to try again. ${helper.getErrorMessage(data)}`, "danger");
        });
    },
    fetchSlipTypes() {
      var self = this;
      self.api
        .getSlipTypes()
        .then((response) => {
          var obj = response.result;
          this.slipTypes = obj.map((state) => ({
            value: state.id,
            label: state.name,
          }));
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't load everything on this page. Refresh to try again. ${helper.getErrorMessage(data)}`, "danger");
        });
    },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            self.selectedSlipType = self.obj.paySlipType;
            self.selectedSlipState = self.obj.status;
            self.payDate = self.obj.payDate || new Date();
            self.paymentDate = self.obj.paymentDate || null;
            self.joinDate = self.obj.joinDate || null;
            self.selectedEmployee = self.obj.profile;

            // Auto-calculate YTD after loading
            if (self.obj.profileId) {
              self.calculateYTD();
            }
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't load the payslip. ${helper.getErrorMessage(data)}`, "danger");
          });
      } else {
        self.obj = self.getEmptyObj();
      }
    },
    onSubmit() {
      var self = this;

      if (!self.selectedEmployee) {
        self.employeeError = "Choose the employee this payslip is for.";
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      self.employeeError = "";
      if (self.saving) return;
      self.saving = true;

      self.obj.paySlipType = self.selectedSlipType;
      self.obj.status = self.selectedSlipState;
      self.obj.payDate = self.payDate;
      self.obj.paymentDate = self.paymentDate;
      self.obj.profileId = self.selectedEmployee.id;

      // Ensure numeric fields are proper numbers
      self.obj.grossAmount = parseFloat(self.obj.grossAmount) || 0;
      self.obj.totalAmount = parseFloat(self.obj.totalAmount) || 0;
      self.obj.employeeEpfAmount = parseFloat(self.obj.employeeEpfAmount) || 0;
      self.obj.employerEpfAmount = parseFloat(self.obj.employerEpfAmount) || 0;
      self.obj.employeeSocsoAmount = parseFloat(self.obj.employeeSocsoAmount) || 0;
      self.obj.employerSocsoAmount = parseFloat(self.obj.employerSocsoAmount) || 0;
      self.obj.employeeEisAmount = parseFloat(self.obj.employeeEisAmount) || 0;
      self.obj.employerEisAmount = parseFloat(self.obj.employerEisAmount) || 0;
      self.obj.taxAmount = parseFloat(self.obj.taxAmount) || 0;
      self.obj.zakatAmount = parseFloat(self.obj.zakatAmount) || 0;

      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.toast("Success", "Payslip created", "success");
            self.$router.push({ path: "/tenants/payslipList" });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't create the payslip. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then((response) => {
            self.toast("Success", "Payslip saved", "success");
            self.$router.push({ path: "/tenants/payslipList" });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't save the payslip. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },
    cancel() {
      this.$router.push({ path: "/tenants/payslipList" });
    },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },
    getEmptyObj() {
      return {
        no: "",
        reference: "",
        payPeriod: "",
        employeeNo: "",
        icNumber: "",
        position: "",
        department: "",
        epfNo: "",
        socsoNo: "",
        taxNo: "",
        bankName: "",
        bankAccountName: "",
        bankAccountNo: "",
        grossAmount: 0,
        totalAmount: 0,
        isEnableEpf: true,
        employeeEpfAmount: 0,
        employerEpfAmount: 0,
        isEnableSocso: true,
        employeeSocsoAmount: 0,
        employerSocsoAmount: 0,
        isEnableEis: true,
        employeeEisAmount: 0,
        employerEisAmount: 0,
        isEnableTax: false,
        taxAmount: 0,
        isEnableZakat: false,
        zakatAmount: 0,
        annualLeaveBalance: 0,
        medicalLeaveBalance: 0,
        sickLeaveBalance: 0,
        paymentReferenceNo: "",
        paymentMethod: "",
        remarks: "",
      };
    },
    submit() {
      this.onSubmit();
      this.submitted = true;
    },
    askChangeState(status) {
      if (!this.obj.id) {
        this.toast("Warning", "Save the payslip first", "warning");
        return;
      }
      this.pendingStatus = status;
      this.statusConfirmModal = true;
    },
    confirmChangeState() {
      var self = this;
      const status = self.pendingStatus;
      if (!status) return;
      self.changingState = true;
      self.obj.status = status.value;
      self.api
        .update(self.obj)
        .then(() => {
          self.toast("Success", `Payslip marked as ${hrStatusLabel("payslip", status.label).toLowerCase()}`, "success");
          self.statusConfirmModal = false;
          self.pendingStatus = null;
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", `Couldn't change the payslip status. ${helper.getErrorMessage(data)}`, "danger");
        })
        .finally(() => {
          self.changingState = false;
        });
    },
  },
};
</script>

<style scoped>
h2.h6 {
  font-weight: 600;
}
</style>
